import { LicenceRecord } from '@lms/shared';

/**
 * Format a value for CSV output
 * Handles nested objects, arrays, and special characters
 */
function formatCsvValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  // Handle objects (like customer_details)
  if (typeof value === 'object' && !Array.isArray(value)) {
    return JSON.stringify(value);
  }

  // Handle arrays (like entitlements)
  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }

  // Convert to string
  const stringValue = String(value);

  // Escape quotes and wrap in quotes if contains comma, newline, or quote
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Generate a timestamped filename for CSV export
 * Format: licences-YYYY-MM-DD-HH-mm-ss.csv
 */
export function generateCsvFilename(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `licences-${year}-${month}-${day}-${hours}-${minutes}-${seconds}.csv`;
}

/**
 * Convert licence records to CSV format
 * Includes all fields from the LicenceRecord schema
 */
export function convertLicencesToCsv(records: LicenceRecord[]): string {
  if (records.length === 0) {
    return '';
  }

  // Define all possible fields in the order they should appear
  const fields: (keyof LicenceRecord)[] = [
    'customer_key',
    'current_account_count',
    'licence_type',
    'status',
    'valid_through',
    'purchaser_aws_account_id',
    'hub_aws_account_id',
    'customer_details',
    'registration_email',
    'last_updated',
    'max_accounts',
    'product_code',
    'session_token',
    'session_expiry',
    'entitlements',
    'version',
  ];

  // Create CSV header
  const header = fields.join(',');

  // Create CSV rows
  const rows = records.map(record => {
    return fields.map(field => formatCsvValue(record[field])).join(',');
  });

  // Combine header and rows
  return [header, ...rows].join('\n');
}

/**
 * Trigger browser download of CSV file
 * 
 * @param csvContent - CSV content as string
 * @param filename - Name of the file to download
 */
export function downloadCsv(csvContent: string, filename: string): void {
  // Create a Blob with CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a temporary download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
}

/**
 * Export licence records to CSV and trigger download
 * 
 * @param records - Array of licence records to export
 * @param filename - Optional custom filename (defaults to timestamped name)
 */
export function exportLicencesToCsv(
  records: LicenceRecord[],
  filename?: string
): void {
  const csvContent = convertLicencesToCsv(records);
  const csvFilename = filename || generateCsvFilename();
  downloadCsv(csvContent, csvFilename);
}
